import React, { useCallback, useMemo, useState } from "react";
import { ConceptItem } from "domains/assets/components/AssetZoom/Aside/SectionConcepts";
import { useHotkeys } from "domains/commons/contexts/HotkeysProvider";
import { sanitizeAndDedupDownloadNames } from "domains/commons/utils/sanitizeAndDedupDownloadNames";
import usePresets from "domains/inference/hooks/usePresets";
import PanelInfoGridItem from "domains/models/components/ModelShow/MoreDetails/PanelInfoGridItem";
import PanelPromptEmbedding from "domains/models/components/ModelShow/MoreDetails/PanelPromptEmbedding";
import TrainImagesItem from "domains/models/components/ModelTrain/ImagesItem";
import { MODEL_TYPE_TO_TITLE } from "domains/models/constants/text";
import useModelTagsManager from "domains/models/hooks/useModelTagsManager";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import TagDisplayWithRemove from "domains/tags/components/TagDisplayWithRemove";
import TagInput from "domains/tags/components/TagInput";
import TagSuggestion from "domains/tags/components/TagSuggestion";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Button from "domains/ui/components/Button";
import Divider from "domains/ui/components/Divider";
import Icon from "domains/ui/components/Icon";
import MarkdownEditor from "domains/ui/components/Markdown/Editor";
import MarkdownViewer from "domains/ui/components/Markdown/Viewer";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from "domains/ui/components/Modal";
import { useHandleApiError } from "infra/api/error";
import {
  GetModelsByModelIdApiResponse,
  useGetModelsDescriptionByModelIdQuery,
  usePutModelsDescriptionByModelIdMutation,
} from "infra/api/generated/api";
import JSZip from "jszip";

import {
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Image,
  SkeletonText,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";

import DefaultPresetDisplay from "./DefaultPresetsDisplay";

const ModelShowMoreDetails = ({
  model,
}: {
  model: GetModelsByModelIdApiResponse["model"];
}) => {
  const { infoToast } = useScenarioToast();
  const { selectedTeamDetails, selectedProject } = useTeamContext();
  const handleApiError = useHandleApiError();
  const [putModelDescriptionTrigger] =
    usePutModelsDescriptionByModelIdMutation();
  const { removeTag, addTags, updateTags } = useModelTagsManager();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDownloadAllLoading, setIsDownloadAllLoading] = useState(false);
  const [trainingImageCurrentIndex, setTrainingImageCurrentIndex] = useState<
    number | undefined
  >();
  const [markdownValue, setMarkdownValue] = useState<string>("");
  const { data: modelDescription, isLoading: isLoadingModelDescription } =
    useGetModelsDescriptionByModelIdQuery({
      projectId: selectedProject.id,
      modelId: model.id,
    });

  const { defaultPreset } = usePresets({
    modelId: model.id,
  });

  const isModelOwned = model?.ownerId === selectedProject.id;

  const trainerEmail = useMemo(() => {
    if (!model.authorId || !isModelOwned) {
      return undefined;
    }

    const teamMember = selectedTeamDetails?.users.find(
      (user) => user.id === model.authorId
    );
    if (teamMember) {
      return teamMember.email;
    }

    const teamApiKey = selectedTeamDetails?.keys.find(
      (key) => key.id === model.authorId
    );
    if (teamApiKey) {
      return `API Key - ${teamApiKey.name}`;
    }

    return model.authorId;
  }, [
    model.authorId,
    selectedTeamDetails?.keys,
    selectedTeamDetails?.users,
    isModelOwned,
  ]);

  const trainDurationFormatted = useMemo(() => {
    const trainDurationInSeconds = model.trainingStats?.trainDuration;
    if (!trainDurationInSeconds) {
      return "-";
    }

    const hours = Math.floor(trainDurationInSeconds / 3_600);
    const minutes = Math.floor((trainDurationInSeconds % 3_600) / 60);

    if (hours >= 1) {
      return `${hours} ${hours === 1 ? "hour" : "hours"} ${minutes} min`;
    }
    return `${minutes} min`;
  }, [model.trainingStats?.trainDuration]);

  const informationItems = useMemo(
    () =>
      [
        {
          label: "Type",
          value: MODEL_TYPE_TO_TITLE[model.type],
        },
        {
          label: "Training duration time",
          value: trainDurationFormatted,
        },
        {
          label: "Training steps",
          value: model.parameters?.maxTrainSteps ?? "-",
        },
        {
          label: "Training images",
          value: model.trainingImagesNumber,
        },
        {
          ...(model.parameters?.learningRate !== undefined
            ? {
                label: "Learning rate",
                value: model.parameters?.learningRate.toExponential() ?? "-",
              }
            : {
                label: "Learning rate UNet",
                value:
                  model.parameters?.learningRateUnet?.toExponential() ?? "-",
              }),
        },
        {
          label: "Text encoder training ratio",
          value: model.parameters?.textEncoderTrainingRatio ?? "-",
        },
        {
          label: "Text encoder learning rate",
          value:
            model.parameters?.learningRateTextEncoder?.toExponential() ?? "-",
        },
        {
          label: "Keyword class phrase",
          value: model.parameters?.classPrompt ?? "-",
        },
        {
          label: "Optimize for",
          value:
            model.parameters?.optimizeFor === "likeness" ? "Portraits" : "-",
        },
      ].filter((item) => item.value && item.value !== "-"),
    [
      model.parameters,
      model.type,
      trainDurationFormatted,
      model.trainingImagesNumber,
    ]
  );

  const isModelOwnedAndWithTrainingImages =
    isModelOwned && !!model.trainingImages?.length;
  const isDescriptionLoadedAndInEditMode =
    !isLoadingModelDescription && isEditMode;
  const isComposition = model.type.includes("-composition");
  const isDescriptionDisplayable =
    !isLoadingModelDescription &&
    modelDescription?.description?.value &&
    !isEditMode;

  // ----------------------------------

  const navigateTrainingImage = useCallback(
    (direction: "prev" | "next") => {
      if (!model.trainingImages || trainingImageCurrentIndex === undefined)
        return;
      setTrainingImageCurrentIndex(
        (trainingImageCurrentIndex +
          (direction === "next" ? 1 : -1) +
          model.trainingImages.length) %
          model.trainingImages.length
      );
    },
    [model.trainingImages, trainingImageCurrentIndex]
  );

  const saveMarkdown = useCallback(
    async (value: string) => {
      try {
        await putModelDescriptionTrigger({
          modelId: model.id,
          projectId: selectedProject.id,
          body: {
            description: value,
          },
        });
      } catch (error) {
        handleApiError(
          error,
          "There was an error saving your model description"
        );
      }
      setIsEditMode(false);
    },
    [putModelDescriptionTrigger, model.id, selectedProject.id, handleApiError]
  );

  const editModeAndSave = useCallback(() => {
    if (isEditMode) {
      void saveMarkdown(markdownValue);
    } else {
      setMarkdownValue(modelDescription?.description?.value ?? "");
    }
    setIsEditMode(!isEditMode);
  }, [
    isEditMode,
    saveMarkdown,
    markdownValue,
    modelDescription?.description?.value,
  ]);

  // ----------------------------------

  const handleDownloadAll = useCallback(async () => {
    const zip = new JSZip();
    const folderName = `${model.name}_dataset`.replace(/ /g, "_");
    const imgFolder = zip.folder(folderName);

    if (!model.trainingImages || !imgFolder) return;

    setIsDownloadAllLoading(true);

    const downloadFilenames = sanitizeAndDedupDownloadNames(
      model.trainingImages.map((image) => ({
        id: image.id,
        name: image.description ? image.description : "training_image",
      })),
      folderName
    );
    const imagePromises = model.trainingImages.map(async (image) => {
      const response = await fetch(
        image.downloadUrl + "&format=jpeg&quality=80"
      );
      const blob = await response.blob();

      const name = downloadFilenames.find((f) => f.id === image.id)?.name ?? "";
      imgFolder.file(name, blob);
    });

    await Promise.all(imagePromises);

    void zip
      .generateAsync({ type: "blob" })
      .then((content) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(content);
        link.download = `${folderName}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {})
      .finally(() => {
        setIsDownloadAllLoading(false);
      });
  }, [model.trainingImages, model.name]);

  const handleCopyDescriptionClick = useCallback(async () => {
    if (!model.trainingImages || trainingImageCurrentIndex === undefined)
      return;
    await navigator.clipboard.writeText(
      model.trainingImages[trainingImageCurrentIndex].description ?? ""
    );
    infoToast({
      title: "Description copied to clipboard",
    });
  }, [infoToast, model.trainingImages, trainingImageCurrentIndex]);

  // ----------------------------------

  useHotkeys("ArrowLeft", () => navigateTrainingImage("prev"), [
    navigateTrainingImage,
  ]);

  useHotkeys("ArrowRight", () => navigateTrainingImage("next"), [
    navigateTrainingImage,
  ]);

  // ----------------------------------

  return (
    <Flex pt={7}>
      <Flex direction="column" flex={1} pr={9}>
        <Flex align="center" pt={4} pb={2}>
          <Text size="title.md">Description</Text>
          {isModelOwned && (
            <IconButton
              ml={3}
              borderRadius={20}
              aria-label="edit"
              icon={<Icon id={isEditMode ? "Ui/Save" : "Ui/Pencil"} h="12px" />}
              onClick={editModeAndSave}
              size="xs"
              variant="secondary"
            />
          )}
        </Flex>

        {isLoadingModelDescription && (
          <SkeletonText pt={2} pb={4} noOfLines={3} />
        )}

        {isDescriptionDisplayable && (
          <Box w="full" pt={2} pb={4}>
            <MarkdownViewer
              {...modelDescription?.description}
              value={modelDescription?.description?.value ?? ""}
              emptyState="No description provided."
            />
          </Box>
        )}
        {isDescriptionLoadedAndInEditMode && (
          <Box w="full" h="full" pb={4}>
            <MarkdownEditor value={markdownValue} setValue={setMarkdownValue} />
          </Box>
        )}
        <Divider />
        {isModelOwnedAndWithTrainingImages && (
          <>
            <Flex align="center" pt={4} pb={2}>
              <Text size="title.md">Training Images</Text>
              <Tooltip label="Download training images" placement="top">
                <IconButton
                  ml={3}
                  borderRadius="full"
                  aria-label="edit"
                  icon={<Icon id={"Ui/Download"} h="12px" />}
                  isLoading={isDownloadAllLoading}
                  onClick={handleDownloadAll}
                  size="xs"
                  variant="secondary"
                />
              </Tooltip>
            </Flex>
            <Grid
              gap={2}
              rowGap={2}
              templateColumns="repeat(auto-fill, 204px)"
              pb={2}
            >
              {model.trainingImages?.map((image, index) => (
                <GridItem
                  key={image.id}
                  cursor="pointer"
                  onClick={() => setTrainingImageCurrentIndex(index)}
                >
                  <TrainImagesItem trainingImage={image} />
                </GridItem>
              ))}
            </Grid>
            {model.trainingImages &&
              trainingImageCurrentIndex !== undefined && (
                <Modal
                  isOpen
                  onClose={() => setTrainingImageCurrentIndex(undefined)}
                  size="2xl"
                  variant="modern"
                >
                  <ModalOverlay />
                  <ModalContent>
                    <ModalCloseButton />
                    <ModalBody pos={"relative"}>
                      <VStack spacing={4}>
                        <Image
                          alt="Training images"
                          rounded="lg"
                          src={`${model.trainingImages[trainingImageCurrentIndex].downloadUrl}&format=jpeg&quality=80`}
                        />
                        <VStack
                          align="start"
                          w="100%"
                          mx={-3}
                          my={-2}
                          px={3}
                          py={2}
                          borderRadius="md"
                          _hover={{
                            bgColor: "background.500",
                            cursor: "pointer",
                          }}
                          onClick={handleCopyDescriptionClick}
                        >
                          <HStack>
                            <Text color="textSecondary" size="body.md">
                              Caption
                            </Text>
                            <Icon id="Ui/Copy" height="12px" />
                          </HStack>
                          <Text color="white" textAlign="start" size="body.md">
                            {model.trainingImages[trainingImageCurrentIndex]
                              .description ?? "No caption"}
                          </Text>
                        </VStack>
                      </VStack>
                    </ModalBody>
                    <Flex
                      pos="absolute"
                      bottom={"50%"}
                      justify="space-between"
                      w="full"
                      px={1.5}
                      transform="translateY(50%)"
                    >
                      <Button
                        rounded="full"
                        w="10px"
                        bg="blackAlpha.900"
                        variant="secondary"
                        onClick={() => navigateTrainingImage("prev")}
                        leftIcon={<Icon id="Ui/ChevronLeft" />}
                      />
                      <Button
                        rounded="full"
                        w="10px"
                        bg="blackAlpha.900"
                        variant="secondary"
                        onClick={() => navigateTrainingImage("next")}
                        rightIcon={<Icon id="Ui/ChevronRight" />}
                      />
                    </Flex>
                  </ModalContent>
                </Modal>
              )}
          </>
        )}
      </Flex>

      <Flex direction="column" gap={6} w="425px" pb={6}>
        {isComposition && (
          <>
            <Text size="title.md">Model Components</Text>
            <Flex direction="column" gap={2}>
              {model.concepts?.map(
                ({ modelId, scale }: { modelId: string; scale: number }) => (
                  <ConceptItem key={modelId} modelId={modelId} scale={scale} />
                )
              )}
            </Flex>
            <Divider />
          </>
        )}
        <Text size="title.md">Parameters</Text>
        <Grid gap={6} rowGap={1} templateColumns="repeat(1, 1fr)">
          <PanelInfoGridItem label="Model ID" value={model.id} copyable />
          {informationItems.map((item) => (
            <GridItem key={item.label}>
              <PanelInfoGridItem label={item.label} value={item.value} />
            </GridItem>
          ))}
          {isModelOwned && trainerEmail && (
            <PanelInfoGridItem label="Author" value={trainerEmail} copyable />
          )}
        </Grid>
        {defaultPreset && (
          <>
            <Divider />
            <Text size="title.md">Default Inference Parameters</Text>
            <DefaultPresetDisplay preset={defaultPreset} model={model} />
          </>
        )}
        {isModelOwned && (
          <>
            <Divider />
            <Text size="title.md">Prompt Embedding</Text>
            <PanelPromptEmbedding model={model} />
          </>
        )}
        <>
          <Divider />
          <Flex direction="column" gap={2}>
            <Text size="title.md">Tags</Text>
            <TagDisplayWithRemove
              tags={model.tags}
              onRemove={
                isModelOwned
                  ? (tagToRemove) => removeTag(model, tagToRemove)
                  : undefined
              }
            />
            {isModelOwned && (
              <>
                <TagInput
                  currentTags={model.tags}
                  onAddTags={(tags) => addTags(model, tags)}
                />
                <TagSuggestion
                  tags={model.tags}
                  onUpdateTags={(newTags) => updateTags(model, newTags)}
                />
              </>
            )}
          </Flex>
        </>
      </Flex>
    </Flex>
  );
};

export default ModelShowMoreDetails;
